import {
  useSticky,
  GET_RD_EXPENSE_PROJECT_ALLOCATIONS,
  RdExpenseProjectAllocationFilter,
  RdExpenseProjectAllocationSort,
  GetRdExpenseProjectAllocationsQuery,
  GetRdExpenseProjectAllocationsQueryVariables,
  useCursorPaginatedQuery,
  Conditions,
  advancedFilter,
  FilterField,
  keywordFilter,
} from '@neo.tax/shared';

const GET_RD_EXPENSE_PROJECT_ALLOCATIONS_KEY = 'GET_RD_EXPENSE_PROJECT_ALLOCATIONS_KEY';

export type RdExpenseProjectAllocation =
  GetRdExpenseProjectAllocationsQuery['getRdExpenseProjectAllocations']['edges'][number]['node'];

interface UseRdExpenseProjectAllocationsProps {
  filterConditions?: Conditions<FilterField<RdExpenseProjectAllocationFilter>>;
  numElementsInitialFetch?: number;
  rdExpenseId: string;
  fiscalYearId: string;
  skip?: boolean;
  sorting?: Array<RdExpenseProjectAllocationSort> | RdExpenseProjectAllocationSort;
  searchKeyword?: string | null;
  debounceKey?: string;
}

export const useRdExpenseProjectAllocations = ({
  debounceKey,
  filterConditions,
  fiscalYearId,
  numElementsInitialFetch,
  rdExpenseId,
  searchKeyword,
  skip = false,
  sorting,
}: UseRdExpenseProjectAllocationsProps): {
  loading: boolean;
  rdExpenseProjectAllocations: RdExpenseProjectAllocation[] | null;
  hasNextPage: boolean;
  onLoadMoreItems: () => Promise<void>;
} => {
  const {
    data: requestData,
    error,
    hasNextPage,
    loading,
    onLoadMoreItems,
  } = useCursorPaginatedQuery<GetRdExpenseProjectAllocationsQuery, GetRdExpenseProjectAllocationsQueryVariables>(
    GET_RD_EXPENSE_PROJECT_ALLOCATIONS,
    {
      variables: {
        rdExpenseId,
        fiscalYearId,
        filter: {
          and: [
            advancedFilter<RdExpenseProjectAllocationFilter>(filterConditions),
            keywordFilter(['projectName'], searchKeyword),
          ],
        },
        sorting: sorting ?? [],
      },
      context: {
        debounceKey: debounceKey || GET_RD_EXPENSE_PROJECT_ALLOCATIONS_KEY,
      },
      skip,
    },
    {
      getConnection: (data) => data.getRdExpenseProjectAllocations,
      numElementsPerFetch: 10,
      numElementsInitialFetch,
    },
  );

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const rdExpenseProjectAllocations =
    stickyData?.getRdExpenseProjectAllocations?.edges.map((edge) => edge.node) ?? null;

  if (loading && !rdExpenseProjectAllocations) {
    return {
      loading: true,
      rdExpenseProjectAllocations: null,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData) {
    return {
      loading: true,
      rdExpenseProjectAllocations: null,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
    };
  }

  return {
    loading,
    rdExpenseProjectAllocations,
    hasNextPage,
    onLoadMoreItems,
  };
};
